// Captain Tasko brand logo. Ship's wheel with a magnifying glass over
// three Captain Tasko symbols (flow chart, markdown page, trend line).
// Inline SVG so it lives in the bundle with no binary-asset dependency
// and recolors cleanly via the `color` CSS property.
//
// To replace with a raster file later: drop a captain-tasko-logo.png
// into apps/web/public/ and import / render via <img> here instead.

interface CaptainTaskoLogoProps {
  size?: number;
  /** Override color. Defaults to the design-token accent blue used
   *  elsewhere in the brand. */
  color?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Decorative use (no aria-label) when the logo sits next to a
   *  textual "Captain Tasko". Default true. */
  decorative?: boolean;
}

export function CaptainTaskoLogo({
  size = 36,
  color = '#2A5C8C',
  className,
  style,
  decorative = true,
}: CaptainTaskoLogoProps) {
  return (
    <svg
      viewBox="0 0 256 256"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      role={decorative ? 'presentation' : 'img'}
      aria-hidden={decorative}
      aria-label={decorative ? undefined : 'Captain Tasko'}
    >
      <g stroke={color} fill="none" strokeLinecap="round" strokeLinejoin="round">
        {/* Ship's wheel — outer rim. */}
        <circle cx="112" cy="112" r="78" strokeWidth="10" />

        {/* 8 spokes radiating outward, each capped with a knob. Spokes
            sit on the cardinal + ordinal axes around the wheel center
            (112, 112). r=78 (rim) → r=104 (knob center). */}
        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
          const rad = (deg * Math.PI) / 180;
          const cx = 112 + Math.cos(rad) * 78;
          const cy = 112 + Math.sin(rad) * 78;
          const ex = 112 + Math.cos(rad) * 104;
          const ey = 112 + Math.sin(rad) * 104;
          return (
            <g key={deg}>
              <line x1={cx} y1={cy} x2={ex} y2={ey} strokeWidth="8" />
              <circle cx={ex} cy={ey} r="7" fill={color} stroke="none" />
            </g>
          );
        })}

        {/* Inner hub — small circle at the wheel center. */}
        <circle cx="112" cy="112" r="6" fill={color} stroke="none" />
      </g>

      {/* Magnifying glass — lens overlaps the lower-right of the wheel,
          handle extends past the rim. White-filled lens so the icons
          inside it read cleanly against the wheel behind. */}
      <g stroke={color} strokeWidth="10" fill="white" strokeLinecap="round">
        <circle cx="160" cy="160" r="58" />
        <line x1="200" y1="200" x2="234" y2="234" />
      </g>

      {/* Three Captain Tasko symbols inside the magnifying glass lens.
          Centered around (160, 160), arranged in a triangle:
            - Top: flow chart (3 small boxes connected)
            - Bottom-left: markdown page (rectangle with M↓)
            - Bottom-right: trend line (3 ascending data points) */}
      <g stroke={color} fill="none" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
        {/* Flow chart — top */}
        <rect x="135" y="128" width="22" height="12" rx="2" />
        <rect x="122" y="148" width="22" height="12" rx="2" />
        <rect x="166" y="148" width="22" height="12" rx="2" />
        <line x1="146" y1="140" x2="133" y2="148" />
        <line x1="146" y1="140" x2="177" y2="148" />

        {/* Markdown page — bottom-left */}
        <rect x="124" y="172" width="22" height="22" rx="2" />
        <text
          x="135"
          y="187"
          fill={color}
          stroke="none"
          fontSize="11"
          fontFamily="monospace"
          fontWeight="700"
          textAnchor="middle"
        >
          M↓
        </text>

        {/* Trend line — bottom-right */}
        <polyline points="160,194 172,184 184,188 196,176" strokeWidth="3" />
        <circle cx="160" cy="194" r="2.5" fill={color} stroke="none" />
        <circle cx="172" cy="184" r="2.5" fill={color} stroke="none" />
        <circle cx="184" cy="188" r="2.5" fill={color} stroke="none" />
        <circle cx="196" cy="176" r="2.5" fill={color} stroke="none" />
      </g>
    </svg>
  );
}
